// Canonical question definitions for the Crowther AI Strategy Audit.
// Options that depend on the respondent's sector are supplied by
// `getOptionsFor(question, sector)`. Backend scoring keys stay canonical.

export const QUESTIONS = [
  {
    key: "Q1",
    type: "single",
    section: "A",
    prompt: "How large is your organisation?",
    options: ["Under 100", "100 to 500", "500 to 2,000", "2,000 to 10,000", "Over 10,000"],
  },
  {
    key: "Q2",
    type: "single",
    section: "A",
    prompt: "Is your organisation regulated?",
    helper: "Prudentially regulated means supervised by a central bank or equivalent authority.",
    options: [
      "Yes, prudentially regulated",
      "Yes, sector-regulated",
      "Not directly regulated",
      "Unsure",
    ],
  },
  {
    key: "Q3",
    type: "single",
    section: "B",
    prompt: "Where is your organisation with AI today?",
    options: [
      "Exploring, nothing deployed",
      "Piloting in one or two areas",
      "In production in limited areas",
      "In production at scale",
    ],
  },
  {
    key: "Q4",
    type: "multi",
    section: "B",
    prompt: "What has been deployed or piloted so far?",
    options: [
      "Chat assistants",
      "Document processing",
      "Customer service automation",
      "Fraud or risk models",
      "Internal copilots",
      "Agents that take actions in systems",
      "Other",
      "None yet",
    ],
    exclusive: "None yet",
    hasOther: true,
  },
  {
    key: "Q5",
    type: "single",
    section: "B",
    prompt: "Where does your organisation's critical data live today?",
    options: [
      "Fully on our own infrastructure",
      "Mostly cloud, in our country or region",
      "Mostly cloud, outside our region",
      "Mixed",
      "Unsure",
    ],
  },
  {
    key: "Q6",
    type: "single",
    section: "B",
    prompt: "How connected are your core systems to each other?",
    options: ["Well integrated", "Partially integrated", "Largely siloed", "Unsure"],
  },
  {
    key: "Q7",
    type: "single",
    section: "B",
    prompt: "Who currently owns AI decisions?",
    options: ["A named executive", "A committee", "IT by default", "Nobody clearly", "Unsure"],
  },
  {
    key: "Q7a",
    type: "single",
    section: "B",
    prompt: "At what level does this sit?",
    options: [
      "CEO or Managing Director",
      "A C-suite executive",
      "A senior leader below C-suite",
      "A dedicated AI or transformation lead",
      "Other",
    ],
    hasOther: true,
    conditional: (answers) => answers.Q7 === "A named executive",
  },
  {
    key: "Q8",
    type: "multi",
    section: "C",
    prompt: "What is driving AI urgency for you right now?",
    options: [
      "Board or shareholder pressure",
      "Cost reduction targets",
      "Competitors moving",
      "Customer expectations",
      "Regulatory deadline",
      "Talent and capacity gaps",
      "Other",
      "No urgent driver yet",
    ],
    exclusive: "No urgent driver yet",
    hasOther: true,
  },
  {
    key: "Q9",
    type: "multi",
    section: "C",
    prompt: "Which functions carry the most manual work today?",
    minSelected: 1,
    hasOther: true,
    // Options resolved from sector via getOptionsFor()
    dynamicBySector: true,
  },
  {
    key: "Q10",
    type: "single",
    section: "C",
    prompt: "Which outcome matters most in the next twelve months?",
    options: ["Grow revenue", "Reduce cost", "Defend or grow market share"],
  },
  {
    key: "Q11",
    type: "multi",
    section: "C",
    prompt: "Which obligations are on your radar?",
    hasOther: true,
    exclusive: "None currently",
    dynamicBySector: true,
    conditional: (answers) =>
      answers.Q2 === "Yes, prudentially regulated" || answers.Q2 === "Yes, sector-regulated",
  },
  {
    key: "Q12",
    type: "single",
    section: "C",
    prompt: "What is your realistic budget posture for AI in the next year?",
    options: [
      "Exploring, no budget yet",
      "Budget for pilots",
      "Budget for one major deployment",
      "Committed transformation budget",
    ],
  },
  {
    key: "Q13",
    type: "text",
    section: "C",
    prompt: "What would make this audit genuinely useful to you?",
    optional: true,
    placeholder: "In a sentence or two, in your own words.",
  },
  // ---- Section D · Physical operations (Physical AI relevance) ----------
  {
    key: "Q14",
    type: "single",
    section: "D",
    prompt: "Does your operation depend on physical machinery, vehicles or field equipment?",
    helper: "This lets us tell you when a hardware-adjacent path would compound your AI value.",
    options: [
      "Yes, extensively (fleet, plant, or site heavy)",
      "Yes, in some functions",
      "Rarely — mostly digital operations",
      "Not at all",
    ],
  },
  {
    key: "Q15",
    type: "multi",
    section: "D",
    prompt: "Which machine classes matter most to your operation today?",
    helper: "Select any that apply. Skip if none.",
    conditional: (answers) =>
      answers.Q14 === "Yes, extensively (fleet, plant, or site heavy)" ||
      answers.Q14 === "Yes, in some functions",
    options: [
      "Factory or production equipment",
      "Warehouse robots and mobile fleets",
      "Heavy vehicles or haulage",
      "Field or agricultural equipment",
      "Cranes, ports or terminal equipment",
      "Mining or quarrying equipment",
      "Sensors and inspection hardware",
      "Other",
    ],
    exclusive: "None",
    hasOther: true,
    optional: true,
  },
  {
    key: "Q16",
    type: "text",
    section: "D",
    prompt: "What is the biggest concern you have about intelligence on your physical operations?",
    helper: "For example: uptime, connectivity, safety, data control, supplier lock in.",
    conditional: (answers) =>
      answers.Q14 &&
      answers.Q14 !== "Not at all",
    optional: true,
    placeholder: "A sentence is fine. Skip if not applicable.",
  },
];

export const SECTION_INTERSTITIALS = {
  A: {
    heading: "About your organisation.",
    body:
      "Two questions to place your organisation. Everything you share stays confidential and shapes only your report.",
    cta: "Begin",
  },
  B: {
    heading: "Where you are today.",
    body:
      "Five questions on your systems, your data, and who decides. Answer as things are, not as you would like them to be. The report is only as honest as its inputs.",
    cta: "Continue",
  },
  C: {
    heading: "Where the pressure is.",
    body:
      "Final section. Six questions on what is driving urgency and where the work concentrates.",
    cta: "Continue",
  },
  D: {
    heading: "Your physical operations.",
    body:
      "A short optional section on hardware and field equipment. If your operation touches physical machines, we can tell you where intelligence on the machine would compound the software work above.",
    cta: "Continue",
  },
};

export const ROLE_OPTIONS = [
  "CEO or Managing Director",
  "CFO",
  "CTO or CDO",
  "CCO or CRO",
  "Chief Compliance Officer",
  "Head of Transformation",
  "Head of Department",
  "Other",
];

export const SECTOR_OPTIONS = [
  "Banking",
  "Insurance",
  "Payments and Fintech",
  "Telecommunications",
  "Healthcare",
  "Energy",
  "Public Sector",
  "Retail",
  "Manufacturing",
  "Other",
];

// ---- Sector-driven option resolvers -------------------------------------

const Q9_BY_SECTOR = {
  Banking: [
    "Operations",
    "Customer service",
    "Compliance and risk",
    "Finance",
    "Sales and distribution",
    "Fraud and financial crime",
    "Collections and recovery",
    "Procurement",
  ],
  "Payments and Fintech": [
    "Operations",
    "Customer service",
    "Compliance and risk",
    "Finance",
    "Fraud and financial crime",
    "Merchant onboarding",
    "Sales and distribution",
    "Procurement",
  ],
  Insurance: [
    "Operations",
    "Customer service",
    "Compliance and risk",
    "Finance",
    "Claims or underwriting",
    "Sales and distribution",
    "HR",
    "Procurement",
  ],
  Telecommunications: [
    "Operations",
    "Customer service",
    "Compliance and risk",
    "Finance",
    "Sales and distribution",
    "Billing and revenue assurance",
    "Field service",
    "Procurement",
  ],
  Healthcare: [
    "Clinical operations",
    "Patient administration",
    "Compliance and risk",
    "Finance",
    "HR",
    "Records management",
    "Procurement",
    "Facilities",
  ],
  Energy: [
    "Operations",
    "Field service",
    "Compliance and risk",
    "Finance",
    "HR",
    "Procurement",
    "Maintenance planning",
    "Customer service",
  ],
  "Public Sector": [
    "Operations",
    "Citizen service",
    "Compliance and risk",
    "Finance",
    "HR",
    "Records management",
    "Procurement",
    "Policy and case work",
  ],
  Retail: [
    "Operations",
    "Customer service",
    "Merchandising",
    "Finance",
    "HR",
    "Supply chain",
    "Marketing",
    "Procurement",
  ],
  Manufacturing: [
    "Operations",
    "Production planning",
    "Quality assurance",
    "Finance",
    "HR",
    "Supply chain",
    "Maintenance",
    "Procurement",
  ],
  Other: [
    "Operations",
    "Customer service",
    "Compliance and risk",
    "Finance",
    "HR",
    "Sales and distribution",
    "Claims or underwriting",
    "Procurement",
  ],
};

// Q11 obligations keep the CANONICAL scoring strings the backend understands,
// but each sector sees the subset that actually applies.
const Q11_BY_SECTOR = {
  Banking: [
    "Data residency and localisation",
    "AI acting on customer data",
    "Transaction monitoring and reporting",
    "Records and audit trails",
    "An upcoming examination or licensing event",
  ],
  "Payments and Fintech": [
    "Data residency and localisation",
    "AI acting on customer data",
    "Transaction monitoring and reporting",
    "Records and audit trails",
    "An upcoming examination or licensing event",
  ],
  Insurance: [
    "Data residency and localisation",
    "AI acting on customer data",
    "Records and audit trails",
    "An upcoming examination or licensing event",
  ],
  Telecommunications: [
    "Data residency and localisation",
    "AI acting on customer data",
    "Records and audit trails",
    "An upcoming examination or licensing event",
  ],
  Healthcare: [
    "Data residency and localisation",
    "AI acting on customer data",
    "Records and audit trails",
    "An upcoming examination or licensing event",
  ],
  Energy: [
    "Data residency and localisation",
    "Records and audit trails",
    "An upcoming examination or licensing event",
  ],
  "Public Sector": [
    "Data residency and localisation",
    "AI acting on customer data",
    "Records and audit trails",
    "An upcoming examination or licensing event",
  ],
  Retail: [
    "Data residency and localisation",
    "AI acting on customer data",
    "Records and audit trails",
  ],
  Manufacturing: [
    "Data residency and localisation",
    "Records and audit trails",
    "An upcoming examination or licensing event",
  ],
  Other: [
    "Data residency and localisation",
    "AI acting on customer data",
    "Transaction monitoring and reporting",
    "Records and audit trails",
    "An upcoming examination or licensing event",
  ],
};

export function getOptionsFor(question, sector) {
  if (question.key === "Q9") {
    const base = Q9_BY_SECTOR[sector] || Q9_BY_SECTOR.Other;
    return [...base, "Other"];
  }
  if (question.key === "Q11") {
    const base = Q11_BY_SECTOR[sector] || Q11_BY_SECTOR.Other;
    return [...base, "Other", "None currently"];
  }
  return question.options || [];
}

export function visibleQuestions(answers) {
  return QUESTIONS.filter((q) => (q.conditional ? q.conditional(answers) : true));
}

// ---- Full ISO 3166-1 alpha-2 country list ------------------------------
// Ordered: pipeline countries first (Nigeria pinned top), then alphabetical.

const PIPELINE = ["NG", "KE", "GH", "ZA", "BB", "JM", "TT"];

const ALL_COUNTRIES = [
  ["AF", "Afghanistan"], ["AL", "Albania"], ["DZ", "Algeria"], ["AD", "Andorra"],
  ["AO", "Angola"], ["AG", "Antigua and Barbuda"], ["AR", "Argentina"], ["AM", "Armenia"],
  ["AU", "Australia"], ["AT", "Austria"], ["AZ", "Azerbaijan"], ["BS", "Bahamas"],
  ["BH", "Bahrain"], ["BD", "Bangladesh"], ["BB", "Barbados"], ["BY", "Belarus"],
  ["BE", "Belgium"], ["BZ", "Belize"], ["BJ", "Benin"], ["BT", "Bhutan"],
  ["BO", "Bolivia"], ["BA", "Bosnia and Herzegovina"], ["BW", "Botswana"], ["BR", "Brazil"],
  ["BN", "Brunei"], ["BG", "Bulgaria"], ["BF", "Burkina Faso"], ["BI", "Burundi"],
  ["CV", "Cabo Verde"], ["KH", "Cambodia"], ["CM", "Cameroon"], ["CA", "Canada"],
  ["CF", "Central African Republic"], ["TD", "Chad"], ["CL", "Chile"], ["CN", "China"],
  ["CO", "Colombia"], ["KM", "Comoros"], ["CG", "Congo"], ["CD", "Congo, DR"],
  ["CR", "Costa Rica"], ["CI", "Côte d’Ivoire"], ["HR", "Croatia"], ["CU", "Cuba"],
  ["CY", "Cyprus"], ["CZ", "Czechia"], ["DK", "Denmark"], ["DJ", "Djibouti"],
  ["DM", "Dominica"], ["DO", "Dominican Republic"], ["EC", "Ecuador"], ["EG", "Egypt"],
  ["SV", "El Salvador"], ["GQ", "Equatorial Guinea"], ["ER", "Eritrea"], ["EE", "Estonia"],
  ["SZ", "Eswatini"], ["ET", "Ethiopia"], ["FJ", "Fiji"], ["FI", "Finland"],
  ["FR", "France"], ["GA", "Gabon"], ["GM", "Gambia"], ["GE", "Georgia"],
  ["DE", "Germany"], ["GH", "Ghana"], ["GR", "Greece"], ["GD", "Grenada"],
  ["GT", "Guatemala"], ["GN", "Guinea"], ["GW", "Guinea-Bissau"], ["GY", "Guyana"],
  ["HT", "Haiti"], ["HN", "Honduras"], ["HK", "Hong Kong"], ["HU", "Hungary"],
  ["IS", "Iceland"], ["IN", "India"], ["ID", "Indonesia"], ["IR", "Iran"],
  ["IQ", "Iraq"], ["IE", "Ireland"], ["IL", "Israel"], ["IT", "Italy"],
  ["JM", "Jamaica"], ["JP", "Japan"], ["JO", "Jordan"], ["KZ", "Kazakhstan"],
  ["KE", "Kenya"], ["KI", "Kiribati"], ["KW", "Kuwait"], ["KG", "Kyrgyzstan"],
  ["LA", "Laos"], ["LV", "Latvia"], ["LB", "Lebanon"], ["LS", "Lesotho"],
  ["LR", "Liberia"], ["LY", "Libya"], ["LI", "Liechtenstein"], ["LT", "Lithuania"],
  ["LU", "Luxembourg"], ["MO", "Macao"], ["MG", "Madagascar"], ["MW", "Malawi"],
  ["MY", "Malaysia"], ["MV", "Maldives"], ["ML", "Mali"], ["MT", "Malta"],
  ["MH", "Marshall Islands"], ["MR", "Mauritania"], ["MU", "Mauritius"], ["MX", "Mexico"],
  ["FM", "Micronesia"], ["MD", "Moldova"], ["MC", "Monaco"], ["MN", "Mongolia"],
  ["ME", "Montenegro"], ["MA", "Morocco"], ["MZ", "Mozambique"], ["MM", "Myanmar"],
  ["NA", "Namibia"], ["NR", "Nauru"], ["NP", "Nepal"], ["NL", "Netherlands"],
  ["NZ", "New Zealand"], ["NI", "Nicaragua"], ["NE", "Niger"], ["NG", "Nigeria"],
  ["KP", "North Korea"], ["MK", "North Macedonia"], ["NO", "Norway"], ["OM", "Oman"],
  ["PK", "Pakistan"], ["PW", "Palau"], ["PS", "Palestine"], ["PA", "Panama"],
  ["PG", "Papua New Guinea"], ["PY", "Paraguay"], ["PE", "Peru"], ["PH", "Philippines"],
  ["PL", "Poland"], ["PT", "Portugal"], ["QA", "Qatar"], ["RO", "Romania"],
  ["RU", "Russia"], ["RW", "Rwanda"], ["KN", "Saint Kitts and Nevis"], ["LC", "Saint Lucia"],
  ["VC", "Saint Vincent and the Grenadines"], ["WS", "Samoa"], ["SM", "San Marino"],
  ["ST", "São Tomé and Príncipe"], ["SA", "Saudi Arabia"], ["SN", "Senegal"], ["RS", "Serbia"],
  ["SC", "Seychelles"], ["SL", "Sierra Leone"], ["SG", "Singapore"], ["SK", "Slovakia"],
  ["SI", "Slovenia"], ["SB", "Solomon Islands"], ["SO", "Somalia"], ["ZA", "South Africa"],
  ["KR", "South Korea"], ["SS", "South Sudan"], ["ES", "Spain"], ["LK", "Sri Lanka"],
  ["SD", "Sudan"], ["SR", "Suriname"], ["SE", "Sweden"], ["CH", "Switzerland"],
  ["SY", "Syria"], ["TW", "Taiwan"], ["TJ", "Tajikistan"], ["TZ", "Tanzania"],
  ["TH", "Thailand"], ["TL", "Timor-Leste"], ["TG", "Togo"], ["TO", "Tonga"],
  ["TT", "Trinidad and Tobago"], ["TN", "Tunisia"], ["TR", "Türkiye"], ["TM", "Turkmenistan"],
  ["TV", "Tuvalu"], ["UG", "Uganda"], ["UA", "Ukraine"], ["AE", "United Arab Emirates"],
  ["GB", "United Kingdom"], ["US", "United States"], ["UY", "Uruguay"], ["UZ", "Uzbekistan"],
  ["VU", "Vanuatu"], ["VA", "Vatican City"], ["VE", "Venezuela"], ["VN", "Vietnam"],
  ["YE", "Yemen"], ["ZM", "Zambia"], ["ZW", "Zimbabwe"],
];

const byCode = Object.fromEntries(ALL_COUNTRIES);
const pipelineRows = PIPELINE.filter((c) => byCode[c]).map((c) => [c, byCode[c]]);
const rest = ALL_COUNTRIES.filter(([c]) => !PIPELINE.includes(c)).sort((a, b) =>
  a[1].localeCompare(b[1])
);

export const COUNTRY_OPTIONS = [...pipelineRows, ...rest];
